import { useState, useRef, useEffect, useContext, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, mediaUrl } from './config';
import { AuthContext } from './AuthContext';
import { formatCurrency } from './currency';
import { IconMessageCircle, IconPackage, IconX, IconChevronDown } from './Icons';

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

// One product the assistant found, rendered as a button rather than a link in
// the reply text: the model is told never to write out URLs, because a tap
// target with the real image, name and current price is both cleaner to read
// and impossible to get wrong. Price formatting goes through the same
// formatCurrency every other card in the app uses, so a chat card and a grid
// card can never show a customer two different numbers for the same product.
function ChatProductCard({ product, onOpen }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="chat-product-card" onClick={() => onOpen(product.id)}>
      {product.image
        ? <img className="chat-product-thumb" src={mediaUrl(product.image)} alt="" loading="lazy" />
        : <span className="chat-product-thumb chat-product-thumb-empty"><IconPackage size={18} /></span>}
      <span className="chat-product-body">
        <span className="chat-product-name">{product.name}</span>
        <span className="chat-product-meta">
          <span className="chat-product-price">{formatCurrency(product.price)}</span>
          {product.wasPrice != null && <s className="chat-product-was">{formatCurrency(product.wasPrice)}</s>}
          {!product.inStock && <span className="chat-product-oos">{t('product.outOfStock')}</span>}
        </span>
      </span>
      <span className="chat-product-cta">{t('chat.viewProduct', 'View')}</span>
    </button>
  );
}

// Support-chat widget, backed by POST /api/support-chat (Claude API).
// Works for guests and signed-in customers alike -- the greeting is purely
// client-side (never sent to the API), so opening the widget never costs a
// request. The conversation itself is kept in memory only (not persisted),
// matching the Messages API's own stateless shape: the full history is
// resent on every turn.
//
// The launcher is a tab anchored to the right edge rather than a floating
// action button. A FAB sits on top of page content wherever the customer has
// scrolled to -- over a product image, over the bottom of a form -- whereas
// the tab stays parked at the edge, out of the way but always findable.
export default function ChatWidget({ open, onOpenChange }) {
  const { t } = useTranslation();
  useContext(AuthContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]); // {role, content, products?}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  // Close on the way out: the customer asked to see the product, so leaving
  // the panel covering it would just mean a second tap to dismiss.
  const openProduct = (id) => {
    onOpenChange(false);
    navigate(`/product/${id}`);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const res = await fetch(`${API_BASE_URL}/api/support-chat`, {
        method: 'POST',
        headers,
        // Only role/content go back to the API -- `products` is render state
        // for this client, and the endpoint validates the message shape.
        body: JSON.stringify({ messages: nextMessages.map(({ role, content }) => ({ role, content })) }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('chat.genericError'));
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: (data.reply || '').trim() || t('chat.emptyReply', "Sorry, I didn't catch that. Could you put it another way?"),
        products: Array.isArray(data.products) ? data.products : [],
      }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          className="chat-side-tab"
          onClick={() => onOpenChange(true)}
          aria-label={t('chat.title')}
          aria-expanded="false"
        >
          <IconMessageCircle size={15} />
          <span className="chat-side-tab-label">{t('chat.tabLabel', 'Support')}</span>
          {/* Points back toward the screen edge the panel slides out of, so
              the tab reads as "pull this open" rather than as a static badge.
              Decorative -- the button's aria-label already names the action. */}
          <span className="chat-side-tab-chevron" aria-hidden="true">
            <IconChevronDown size={12} />
          </span>
        </button>
      )}

      {open && (
        <div className="chat-widget-wrap">
          <div className="chat-widget-panel" role="dialog" aria-label={t('chat.title')}>
            <div className="chat-widget-header">
              <span className="chat-widget-header-title"><IconMessageCircle size={16} /> {t('chat.title')}</span>
              <button className="chat-widget-close" onClick={() => onOpenChange(false)} aria-label={t('common.close', 'Close')}>
                <IconX size={16} />
              </button>
            </div>

            <div className="chat-widget-body" ref={scrollRef}>
              <div className="chat-widget-msg chat-widget-msg-assistant">
                {t('chat.greeting')}
              </div>
              {messages.map((m, i) => (
                <Fragment key={i}>
                  <div className={`chat-widget-msg chat-widget-msg-${m.role}`}>
                    {m.content}
                  </div>
                  {m.products?.length > 0 && (
                    <div className="chat-product-list">
                      {m.products.map((p) => (
                        <ChatProductCard key={p.id} product={p} onOpen={openProduct} />
                      ))}
                    </div>
                  )}
                </Fragment>
              ))}
              {sending && (
                <div className="chat-widget-msg chat-widget-msg-assistant chat-widget-typing">
                  <span /><span /><span />
                </div>
              )}
            </div>

            {error && <div className="chat-widget-error">{error}</div>}

            <form className="chat-widget-input-row" onSubmit={send}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.placeholder')}
                disabled={sending}
                maxLength={4000}
              />
              <button type="submit" disabled={sending || !input.trim()} aria-label={t('chat.send')}>
                <SendIcon />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
